import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useToastError } from "../hooks/useToast";
import { setIsLogginProcess, setIsLoggingOut, setLoginCompleted } from "../reduxSlices/globalFlagsSlice";
import { setUnknownUser } from "../reduxSlices/parametersSlice";
import {
  setCarrearActual,
  setDebeAlertarSuperposicion,
  setListadoCarreras,
  setStudentBloqueoActivo,
  setStudentBloqueoMensajes,
  setStudentInicializado,
  setStudentMostrarHorarios,
  setStudentMostrarAulas,
  setToken,
} from "../reduxSlices/studentSlice";
import { setGetCuposAlumnoFetched } from "../reduxSlices/subjectsSlice";
import { EstadoLoginStudent } from "../types";
import { clearAutoLogout, getIpoNotInWhitelistMessage, getRedirectUri, isIpoNotInWhitelistError, registerLogoutCallback, scheduleAutoLogout, setupVisibilityTokenCheck } from "../utils/authHelpers";
import { clearSessionSnapshot } from "../utils/sessionSnapshot";
import { useMsal } from "@azure/msal-react";

interface AuthHookResult {
  isAuthenticating: boolean;
  multicarrera: boolean;
  carrerasList: any[];
  selectingCarrera: boolean;
  inicializoConMultiCarrera: boolean;
  setMulticarrera: (value: boolean) => void;
  setIsAuthenticating: (value: boolean) => void;
  setCarrerasList: (value: any[]) => void;
  setSelectingCarrera: (value: boolean) => void;
  setInicializoConMultiCarrera: (value: boolean) => void;
  handleAuthResponse: (response: EstadoLoginStudent, isCampus?: boolean) => Promise<void>;
  saveToken: (token: string) => Promise<void>;
  ingresarEnApp: (
    token: string,
    mostrarHorarios: boolean,
    inicializando: boolean,
    idAP?: number,
    mostrarAulas?: boolean
  ) => Promise<void>;
  ingresarEnAppMulticarrera: (
    token: string,
    mostrarHorarios: boolean,
    inicializando: boolean,
    idAP?: number,
    mostrarAulas?: boolean
  ) => Promise<void>;
  seleccionaCarrera: (
    idAlu: number,
    nombreCarrera: string,
    seleccionaCarreraApi: (idAlu: number) => Promise<EstadoLoginStudent>
  ) => Promise<void>;
  doLogout: () => Promise<void>;
}

export const useAuth = (): AuthHookResult => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [multicarrera, setMulticarrera] = useState(false);
  const [carrerasList, setCarrerasList] = useState<any[]>([]);
  const [selectingCarrera, setSelectingCarrera] = useState(false);
  const [inicializoConMultiCarrera, setInicializoConMultiCarrera] = useState(false);
  const { instance } = useMsal();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showError = useToastError();

  const saveToken = async (token: string) => {
    localStorage.setItem("token", token);
    await dispatch(setToken(token));
    scheduleAutoLogout(token);
  };

  const ingresarEnApp = async (
    token: string,
    mostrarHorarios: boolean,
    inicializando: boolean,
    idAP?: number,
    mostrarAulas?: boolean
  ) => {
    await saveToken(token);
    dispatch(setLoginCompleted(true));
    if (idAP) localStorage.setItem("idAP", JSON.stringify(idAP));
    if (mostrarHorarios) dispatch(setStudentMostrarHorarios(mostrarHorarios));
    if (typeof mostrarAulas === "boolean") dispatch(setStudentMostrarAulas(mostrarAulas));

    if (inicializando) {
      await dispatch(setStudentInicializado(1));
      navigate("/inicializacion");
    } else {
      await dispatch(setStudentInicializado(2));
      if (!multicarrera) {
        navigate("/");
      } else if (multicarrera && inicializoConMultiCarrera) {
        navigate("/");
      }
    }

    dispatch(setIsLogginProcess(false));
  };

  const ingresarEnAppMulticarrera = async (
    token: string,
    mostrarHorarios: boolean,
    inicializando: boolean,
    idAP?: number,
    mostrarAulas?: boolean
  ) => {
    await saveToken(token);
    if (idAP) localStorage.setItem("idAP", JSON.stringify(idAP));
    if (mostrarHorarios) dispatch(setStudentMostrarHorarios(mostrarHorarios));
    if (typeof mostrarAulas === "boolean") dispatch(setStudentMostrarAulas(mostrarAulas));

    if (inicializando) {
      await dispatch(setStudentInicializado(1));
    } else {
      await dispatch(setStudentInicializado(2));
    }

    dispatch(setLoginCompleted(true));
    dispatch(setIsLogginProcess(false));
    navigate("/");
  };

  const seleccionaCarrera = async (
    idAlu: number,
    nombreCarrera: string,
    seleccionaCarreraApi: (idAlu: number) => Promise<EstadoLoginStudent>
  ) => {
    try {
      const res: EstadoLoginStudent = await seleccionaCarreraApi(idAlu);

      if (res?.error) {
        if (isIpoNotInWhitelistError(res.error)) {
          showError(getIpoNotInWhitelistMessage(res.error));
        } else {
          showError("Error al seleccionar la carrera, por favor intente nuevamente.");
        }
        setSelectingCarrera(false);
        dispatch(setIsLogginProcess(false));
        return;
      }

      const { token, inicializando, mostrarHorarios, mostrarAulas } = res;
      dispatch(setCarrearActual(nombreCarrera));
      ingresarEnAppMulticarrera(token, mostrarHorarios, inicializando, idAlu, mostrarAulas);
      setSelectingCarrera(false);
      dispatch(setIsLogginProcess(false));
    } catch (error) {
      console.error("Error al seleccionar carrera:", error);
      if (isIpoNotInWhitelistError(error)) {
        showError(getIpoNotInWhitelistMessage(error));
      } else {
        showError("Error al seleccionar la carrera, por favor intente nuevamente.");
      }
      setSelectingCarrera(false);
    }
  };

  const handleAuthResponse = useCallback(
    async (res: EstadoLoginStudent, isCampus: boolean = false) => {
      dispatch(setIsLogginProcess(true));
      const {
        token,
        inicializando,
        mostrarHorarios,
        mostrarAulas,
        debeElegirCarrera,
        carreras,
        cantidadCarreras,
        bloqueado,
        mensajeBloqueo,
        contactoBloqueo,
        debeAlertarSuperposicion,
        error,
      } = res || {};
      
      if (debeAlertarSuperposicion) {
        dispatch(setDebeAlertarSuperposicion(debeAlertarSuperposicion));
      }

      const campusToken = isCampus ? localStorage.getItem("campusToken") : null;
      if (error) {
        if (error.response?.status === 401) {
          const isIpoWhitelist = isIpoNotInWhitelistError(error);
          const errorMessage = isIpoWhitelist
            ? getIpoNotInWhitelistMessage(error)
            : isCampus
              ? "Link incorrecto o expirado."
              : "Email incorrecto o no permitido.";

          showError(errorMessage);
          console.log(isIpoWhitelist ? "ERROR IPO_NOT_IN_WHITELIST>>" : "ERROR 401>> REDIRIGIENDO A LOGIN", {
            token,
            campusToken,
          });

          if (!isIpoWhitelist) {
            clearSessionSnapshot();
          }

          if (isCampus) {
            setTimeout(() => {
              window.location.href = getRedirectUri();
            }, 2000);
          } else {
            navigate("/login");
          }
          setIsAuthenticating(false);
          dispatch(setIsLogginProcess(false));
          return;
        }
        if (error.response?.status === 404) {
          dispatch(setUnknownUser(true));
          navigate("/usuario");
          setIsAuthenticating(false);
          dispatch(setIsLogginProcess(false));
          return;
        }
        return;
      }

      if (bloqueado) {
        await saveToken(token);

        if (isCampus && campusToken) {
          localStorage.setItem("campusToken", campusToken);
        }
        dispatch(setStudentBloqueoActivo(bloqueado));
        dispatch(setStudentBloqueoMensajes({ mensaje: mensajeBloqueo, subMensaje: contactoBloqueo }));
        navigate("/bloqueo");
        setIsAuthenticating(false);
        dispatch(setIsLogginProcess(false));
        return;
      }
      // Seleccion de carrera
      if (token && debeElegirCarrera && cantidadCarreras > 1 && !inicializando) {
        dispatch(setListadoCarreras(carreras));
        setCarrerasList(carreras);
        setMulticarrera(true);
        await saveToken(token);

        if (isCampus && campusToken) {
          localStorage.setItem("campusToken", campusToken);
        }
        navigate("/carrera", {
          state: {
            carreras: carreras,
            mostrarHorarios: mostrarHorarios,
          },
        });
        return;
      }
      // Inicializacion de la app
      if (token && cantidadCarreras < 2 && !inicializando && !multicarrera) {
        let iAP = undefined;
        if (carreras?.length > 0) {
          iAP = carreras[0]?.idAlumnoPrograma;
          dispatch(setCarrearActual(carreras[0].descripcionCarrera));
          dispatch(setListadoCarreras(carreras));
        }

        if (isCampus && campusToken) {
          localStorage.setItem("campusToken", campusToken);
        }
        await ingresarEnApp(token, mostrarHorarios, inicializando, iAP, mostrarAulas);
        return;
      }

      if (token && inicializando && !multicarrera) {
        if (isCampus && campusToken) {
          localStorage.setItem("campusToken", campusToken);
        }
        await ingresarEnApp(token, mostrarHorarios, inicializando, undefined, mostrarAulas);
        return;
      }

      dispatch(setIsLogginProcess(false));
    },
    [dispatch, navigate, showError, setCarrerasList, setMulticarrera, ingresarEnApp, multicarrera]
  );

  const clearStorageExcept = (keysToPreserve: string[]) => {
    const preservedLocal: { [key: string]: string } = {};
    const preservedSession: { [key: string]: string } = {};

    keysToPreserve.forEach((key) => {
      const localValue = localStorage.getItem(key);
      const sessionValue = sessionStorage.getItem(key);
      if (localValue) preservedLocal[key] = localValue;
      if (sessionValue) preservedSession[key] = sessionValue;
    });

    localStorage.clear();
    sessionStorage.clear();

    Object.entries(preservedLocal).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    Object.entries(preservedSession).forEach(([key, value]) => {
      sessionStorage.setItem(key, value);
    });
  };

  const doLogoutRef = useRef<() => Promise<void>>(async () => {});

  const doLogout = async () => {
    clearAutoLogout();

    try {
      const redirectUri = getRedirectUri();
      const isCampus = !!localStorage.getItem("campusToken");
      dispatch(setIsLoggingOut(true));
      clearSessionSnapshot();
      clearStorageExcept([]);
      dispatch(setToken(""));
      dispatch(setCarrearActual(""));
      dispatch(setListadoCarreras([]));
      dispatch(setStudentInicializado(0));
      dispatch(setStudentMostrarHorarios(false));
      dispatch(setStudentMostrarAulas(true));
      dispatch(setStudentBloqueoActivo(false));
      dispatch(setStudentBloqueoMensajes({ mensaje: "", subMensaje: "" }));
      dispatch(setDebeAlertarSuperposicion(false));
      dispatch(setIsLogginProcess(false));
      dispatch(setGetCuposAlumnoFetched(false));
      dispatch(setLoginCompleted(false));

      if (!isCampus) {
        await instance.logoutRedirect({
          postLogoutRedirectUri: redirectUri,
        });
      } else {
        window.location.href = redirectUri;
      }
    } catch (error) {
      console.error("Error durante el logout:", error);
      showError("Error al cerrar sesión, por favor intenta nuevamente.");
    }
  };

  doLogoutRef.current = doLogout;

  useEffect(() => {
    registerLogoutCallback(() => doLogoutRef.current());

    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      scheduleAutoLogout(storedToken);
    }

    return setupVisibilityTokenCheck();
  }, []);

  return {
    isAuthenticating,
    multicarrera,
    carrerasList,
    selectingCarrera,
    inicializoConMultiCarrera,
    setIsAuthenticating,
    setMulticarrera,
    setCarrerasList,
    setSelectingCarrera,
    setInicializoConMultiCarrera,
    handleAuthResponse,
    saveToken,
    ingresarEnApp,
    ingresarEnAppMulticarrera,
    seleccionaCarrera,
    doLogout,
  };
};
